import { useEffect, useState, Fragment } from "react";
import {
  Calendar,
  FileDown,
  ChevronDown,
  ChevronRight,
  Clock,
  Search as SearchIcon,
  Printer,
  Activity,
  Trash2,
  Pencil,
  X,
} from "lucide-react";
import { getOrdersForMonth, getMonthKey, deleteOrder, updateOrder } from "../../services/orderService";
import { getUserById, getAllUsers, updateUserDetails } from "../../services/userService";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import InvoiceReceipt from "../../components/invoice/InvoiceReceipt";

const AdminRechargeList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Pagination states (Local Client-Side)
  const [visibleCount, setVisibleCount] = useState(25);
  const [pageLoading, setPageLoading] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransactionType, setSelectedTransactionType] = useState("All");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("All");
  const [dateFilter, setDateFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Edit Modal State
  const [editingTxn, setEditingTxn] = useState(null);
  const [editFormData, setEditFormData] = useState({ type: "", paymentMethod: "", shakeBalanceChange: "", remainingShakesAfter: "" });
  const [isUpdating, setIsUpdating] = useState(false);

  // Month filter state
  const generateMonthOptions = () => {
    const options = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      options.push(d.toLocaleString('en-US', { month: 'short' }) + '-' + d.getFullYear());
      d.setMonth(d.getMonth() - 1);
    }
    return options;
  };
  const [monthOptions] = useState(generateMonthOptions());
  const [selectedMonth, setSelectedMonth] = useState(getMonthKey());
  const [collapsedDays, setCollapsedDays] = useState({});

  const toggleDayCollapse = (dateStr) => {
    setCollapsedDays((prev) => ({ ...prev, [dateStr]: !prev[dateStr] }));
  };

  // Transaction types
  const transactionTypes = [
    "All",
    "TopUp Guest Shake",
    "TopUp Associate Shake",
    "Guest Shake",
    "Associate Shake",
    "Trial",
    "RetailShake",
    "Donation",
    "Sample",
  ];

  useEffect(() => {
    fetchTransactions();
  }, [selectedMonth]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      // Fetch only the selected month's orders and all users
      const [orders, users] = await Promise.all([
        getOrdersForMonth(selectedMonth),
        getAllUsers()
      ]);

      // Create O(1) lookup map for users
      const userMap = {};
      users.forEach(u => { userMap[u.id] = u; });

      // ── Reconstruct remainingShakesAfter for orders missing it ──────────
      // Group orders by userId
      const ordersByUser = {};
      orders.forEach(order => {
        if (!ordersByUser[order.userId]) ordersByUser[order.userId] = [];
        ordersByUser[order.userId].push(order);
      });

      // For each user, walk newest → oldest and compute running balance
      const reconstructedMap = {}; // docId → computed remainingShakesAfter

      Object.entries(ordersByUser).forEach(([userId, userOrders]) => {
        const user = userMap[userId];
        // Start from the user's current live balance
        let runningBalance = user?.inventory?.remainingShakes ?? 0;

        // Sort newest → oldest
        const newestFirst = [...userOrders].sort((a, b) => {
          const da = new Date(a.timestamp || a.createdAt || 0);
          const db2 = new Date(b.timestamp || b.createdAt || 0);
          return db2 - da;
        });

        newestFirst.forEach(order => {
          const stored = order.summary?.remainingShakesAfter;
          const change = order.summary?.shakeBalanceChange ?? 0;

          if (stored !== undefined && stored !== null) {
            // Stored value is the ground truth — use it and re-sync runningBalance
            reconstructedMap[order.id] = stored;
            // Before this order the balance was: stored - change
            runningBalance = stored - change;
          } else {
            // No stored value — assign current running balance as the "after" for this order
            reconstructedMap[order.id] = runningBalance;
            // Step back: undo this order's change to get balance before it
            runningBalance = runningBalance - change;
          }
        });
      });

      // ── Enrich orders with user data + reconstructed shake balance ───────
      const enrichedOrders = orders.map((order) => {
        try {
          const user = userMap[order.userId];
          const discountAmount = order.items?.reduce((sum, item) => {
            const mrp = item.mrp || item.originalPrice || item.pricePerUnit || 0;
            const price = item.price || item.productPrice || item.pricePerUnit || 0;
            return sum + (Math.max(0, mrp - price) * item.quantity);
          }, 0) || 0;

          // Prefer stored value, then reconstructed, then null
          const remainingShakes =
            (order.summary?.remainingShakesAfter !== undefined && order.summary.remainingShakesAfter !== null)
              ? order.summary.remainingShakesAfter
              : (reconstructedMap[order.id] ?? null);

          return {
            ...order,
            userName: user?.personalInformation?.fullName || user?.personalDetails?.name || user?.displayName || "Unknown",
            userMobile: user?.personalInformation?.mobileNumber || user?.personalDetails?.mobile || user?.mobileNumber || "N/A",
            userEmail: user?.personalInformation?.email || user?.personalDetails?.email || user?.email || "N/A",
            sponsoredBy: user?.sponsoredBy || user?.accountSecurity?.sponsoredBy || "N/A",
            remainingShakes,
            discountAmount,
            weight: order.dailyHealthRecord?.weight || "N/A",
          };
        } catch (error) {
          console.error(`Error enriching order ${order.orderId}:`, error);
          return { ...order, userName: "Unknown", userMobile: "N/A", userEmail: "N/A" };
        }
      });

      setTransactions(enrichedOrders);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };
  // Get date range based on filter
  const getDateRange = (filter) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case "Today":
        return {
          startDate: today.toISOString(),
          endDate: new Date().toISOString(),
        };
      case "This Week": {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return {
          startDate: weekStart.toISOString(),
          endDate: new Date().toISOString(),
        };
      }
      case "This Month": {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          startDate: monthStart.toISOString(),
          endDate: new Date().toISOString(),
        };
      }
      case "Custom":
        return {
          startDate: fromDate ? new Date(fromDate).toISOString() : null,
          endDate: toDate ? new Date(toDate).toISOString() : null,
        };
      default:
        return {};
    }
  };

  // Handle search/filter
  const handleSearch = () => {
    setVisibleCount(25); // Apply client-side filters and reset pagination
    toast.success("Filters applied");
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedTransactionType("All");
    setSelectedPaymentMethod("All");
    setDateFilter("All");
    setFromDate("");
    setToDate("");
    setSelectedMonth(getMonthKey());
    setVisibleCount(25); // Reset pagination on clear
  };

  // Delete transaction
  const handleDeleteTransaction = async (txnId, monthKey, dayKey) => {
    if (window.confirm("Are you sure you want to delete this transaction? This action cannot be undone.")) {
      try {
        await deleteOrder(txnId, monthKey, dayKey);
        toast.success("Transaction deleted successfully");
        fetchTransactions();
      } catch (error) {
        console.error("Error deleting transaction:", error);
        toast.error("Failed to delete transaction");
      }
    }
  };

  // Open Edit Modal
  const handleEditClick = (txn) => {
    setEditingTxn(txn);
    setEditFormData({
      type: txn.transaction?.type || "",
      paymentMethod: txn.transaction?.paymentMethod || "",
      shakeBalanceChange: txn.summary?.shakeBalanceChange !== undefined ? String(txn.summary.shakeBalanceChange) : "",
      remainingShakesAfter: txn.remainingShakes !== null && txn.remainingShakes !== undefined ? String(txn.remainingShakes) : "",
    });
  };

  // Submit Edit
  const handleUpdateTransaction = async () => {
    if (!editingTxn) return;
    setIsUpdating(true);
    try {
      const newShakeBalanceChange = editFormData.shakeBalanceChange !== "" ? parseInt(editFormData.shakeBalanceChange, 10) : undefined;
      const newRemainingShakesAfter = editFormData.remainingShakesAfter !== "" ? parseInt(editFormData.remainingShakesAfter, 10) : undefined;

      const updatedData = {
        "transaction.type": editFormData.type,
        "transaction.paymentMethod": editFormData.paymentMethod,
        ...(newShakeBalanceChange !== undefined && { "summary.shakeBalanceChange": newShakeBalanceChange }),
        ...(newRemainingShakesAfter !== undefined && { "summary.remainingShakesAfter": newRemainingShakesAfter }),
      };

      await updateOrder(editingTxn.id, editingTxn.monthKey, editingTxn.dayKey, updatedData);

      // If Rem. Shakes was changed AND this is the most recent order for this user,
      // update the user's live inventory balance so the POS sidebar is also correct.
      if (newRemainingShakesAfter !== undefined && editingTxn.userId) {
        try {
          await updateUserDetails(editingTxn.userId, {
            "inventory.remainingShakes": newRemainingShakesAfter,
          });
        } catch (e) {
          console.warn("Could not update user inventory balance:", e);
        }
      }

      toast.success("Transaction updated successfully");
      setEditingTxn(null);
      fetchTransactions();
    } catch (error) {
      console.error("Error updating transaction:", error);
      toast.error("Failed to update transaction");
    } finally {
      setIsUpdating(false);
    }
  };

  // Filter transactions by search term, type, and date
  const filteredTransactions = transactions.filter((txn) => {
    // 1. Search Filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      txn.orderId?.toLowerCase().includes(searchLower) ||
      txn.userName?.toLowerCase().includes(searchLower) ||
      txn.userMobile?.includes(searchTerm) ||
      txn.transaction?.type?.toLowerCase().includes(searchLower);

    if (!matchesSearch) return false;

    // 2. Transaction Type Filter
    const matchesType = selectedTransactionType === "All" ||
      txn.transaction?.type === selectedTransactionType;

    if (!matchesType) return false;

    // 2.1 Payment Method Filter
    const matchesPayment = selectedPaymentMethod === "All" ||
      txn.transaction?.paymentMethod?.toLowerCase() === selectedPaymentMethod.toLowerCase();

    if (!matchesPayment) return false;

    // 3. Date Filter
    if (dateFilter === "All") return true;

    const txnDate = new Date(txn.timestamp || txn.createdAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === "Today") {
      return txnDate >= today;
    }

    if (dateFilter === "This Week") {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      return txnDate >= weekStart;
    }

    if (dateFilter === "This Month") {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return txnDate >= monthStart;
    }

    if (dateFilter === "Custom") {
      let matches = true;
      if (fromDate) {
        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);
        matches = matches && txnDate >= start;
      }
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        matches = matches && txnDate <= end;
      }
      return matches;
    }

    return true;
  });

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredTransactions.map((txn) => ({
      "Order ID": txn.orderId || "N/A",
      Date: formatDate(txn.timestamp || txn.createdAt),
      "User Name": txn.userName || "N/A",
      Mobile: txn.userMobile || "N/A",
      "Transaction Type": txn.transaction?.type || "N/A",
      Products:
        txn.items
          ?.map((item) => `${item.productName} (${item.quantity})`)
          .join(", ") || "N/A",
      "Total Amount": txn.summary?.grandTotalAmount || 0,
      "Total VP": txn.summary?.totalOrderVP || 0,
      "Shake Balance": txn.summary?.shakeBalanceChange || 0,
      "Payment Method": txn.transaction?.paymentMethod || "N/A",
      Status: txn.transaction?.status || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recharge Transactions");
    XLSX.writeFile(
      wb,
      `Recharge_Transactions_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    toast.success("Exported to Excel");
    setShowExportMenu(false);
  };

  // Helper: format date as DD/MM/YYYY
  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Consolidated Excel Export — matches exact image format
  const exportConsolidatedExcel = () => {
    const wb = XLSX.utils.book_new();
    const rows = [];
    const cols = ["Date", "Bill No", "Name", "SalesType", "Amount", "Remaining Shakes"];

    // Period header
    const startStr = fromDate ? formatShortDate(fromDate) : formatShortDate(new Date().toISOString());
    const endStr = toDate ? formatShortDate(toDate) : formatShortDate(new Date().toISOString());
    rows.push([`For the Period Of Sales Bill Consolidate From Date ${startStr} To Date ${endStr}`, "", "", "", "", ""]);
    rows.push(["", "", "", "", "", ""]); // blank row

    // Column header row
    rows.push(cols);

    const types = [...new Set(filteredTransactions.map(txn => txn.transaction?.type || "N/A"))];

    types.forEach(type => {
      const typeTxns = filteredTransactions.filter(txn => (txn.transaction?.type || "N/A") === type);
      const groupCount = typeTxns.length;
      const groupTotal = typeTxns.reduce((sum, txn) => sum + (txn.summary?.grandTotalAmount || 0), 0);

      // Dashed separator
      rows.push(["---", "---", "---", "---", "---", "---"]);
      // Group name row
      rows.push([type.toUpperCase(), "", "", "", "", ""]);

      // Data rows
      typeTxns.forEach(txn => {
        rows.push([
          formatShortDate(txn.timestamp || txn.createdAt),
          txn.orderId || "N/A",
          txn.userName || "N/A",
          (txn.transaction?.paymentMethod || "CASH").toUpperCase(),
          parseFloat((txn.summary?.grandTotalAmount || 0).toFixed(2)),
          txn.remainingShakes ?? ""
        ]);
      });

      // Dashed separator
      rows.push(["---", "---", "---", "---", "---", "---"]);
      // Group footer
      rows.push([
        "Group Count :",
        groupCount,
        "GroupWise Total",
        "",
        parseFloat(groupTotal.toFixed(2)),
        ""
      ]);
    });

    // Overall grand total rows
    const grandTotal = filteredTransactions.reduce((sum, txn) => sum + (txn.summary?.grandTotalAmount || 0), 0);
    const grandCount = filteredTransactions.length;
    rows.push(["===", "===", "===", "===", "===", "==="]);
    rows.push([
      `Overall Total (${grandCount} transactions)`,
      "",
      "",
      "",
      parseFloat(grandTotal.toFixed(2)),
      ""
    ]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 14 }, { wch: 10 }, { wch: 26 }, { wch: 14 },
      { wch: 14 }, { wch: 18 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, "Consolidated Report");
    XLSX.writeFile(wb, `Consolidated_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Consolidated Excel Exported");
    setShowExportMenu(false);
  };

  // Consolidated PDF Export — Portrait A4, clean design
  const exportConsolidatedPDF = () => {
    const doc = new jsPDF(); // Default is portrait A4

    const startStr = fromDate ? formatShortDate(fromDate) : formatShortDate(new Date().toISOString());
    const endStr = toDate ? formatShortDate(toDate) : formatShortDate(new Date().toISOString());
    const pageW = doc.internal.pageSize.getWidth();

    // ── Header block ─────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(30, 30, 30);
    doc.text("Sales Bill Consolidated Report", pageW / 2, 16, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    doc.text(
      `For the Period: ${startStr}  –  ${endStr}`,
      pageW / 2, 23, { align: "center" }
    );

    // Thin horizontal rule below header
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(10, 27, pageW - 10, 27);

    // ── Build table body ──────────────────────────────────────
    const types = [...new Set(filteredTransactions.map(txn => txn.transaction?.type || "N/A"))];
    const body = [];
    const RED = [180, 0, 0];
    const GRAY_LINE = [210, 210, 210];

    types.forEach((type) => {
      const typeTxns = filteredTransactions.filter(txn => (txn.transaction?.type || "N/A") === type);
      const groupCount = typeTxns.length;
      const groupTotal = typeTxns.reduce((sum, txn) => sum + (txn.summary?.grandTotalAmount || 0), 0);

      // Group name row (red bold, full span)
      body.push([
        {
          content: type.toUpperCase(),
          colSpan: 6,
          styles: {
            fontStyle: 'bold',
            textColor: RED,
            fillColor: [255, 248, 248],
            fontSize: 8.5,
            cellPadding: { top: 3, bottom: 3, left: 3, right: 3 }
          }
        }
      ]);

      // Data rows
      typeTxns.forEach(txn => {
        body.push([
          formatShortDate(txn.timestamp || txn.createdAt),
          txn.orderId || "N/A",
          txn.userName || "N/A",
          (txn.transaction?.paymentMethod || "CASH").toUpperCase(),
          { content: (txn.summary?.grandTotalAmount || 0).toFixed(2), styles: { halign: 'right' } },
          { content: String(txn.remainingShakes ?? ""), styles: { halign: 'right' } }
        ]);
      });

      // Group footer (red bold)
      body.push([
        { content: `Group Count :  ${groupCount}`, colSpan: 2, styles: { fontStyle: 'bold', textColor: RED, cellPadding: { top: 3, bottom: 3, left: 3, right: 3 } } },
        { content: "GroupWise Total", colSpan: 2, styles: { fontStyle: 'bold', textColor: RED, cellPadding: { top: 3, bottom: 3 } } },
        { content: groupTotal.toFixed(2), styles: { fontStyle: 'bold', textColor: RED, halign: 'right', cellPadding: { top: 3, bottom: 3 } } },
        { content: "", styles: { fontStyle: 'bold', textColor: RED } }
      ]);

      // Thin spacer row between groups
      body.push([
        { content: "", colSpan: 6, styles: { fillColor: [240, 240, 240], minCellHeight: 2, cellPadding: 0 } }
      ]);
    });

    // ── Overall Grand Total row ───────────────────────────────
    const grandTotal = filteredTransactions.reduce((sum, txn) => sum + (txn.summary?.grandTotalAmount || 0), 0);
    const grandCount = filteredTransactions.length;
    const NAVY = [15, 23, 42];
    body.push([
      { content: "", colSpan: 6, styles: { fillColor: [210, 210, 210], minCellHeight: 1.5, cellPadding: 0 } }
    ]);
    body.push([
      { content: `Overall Total  (${grandCount} transactions)`, colSpan: 4, styles: { fontStyle: 'bold', textColor: [255, 255, 255], fillColor: NAVY, fontSize: 9, cellPadding: { top: 4, bottom: 4, left: 5, right: 3 } } },
      { content: grandTotal.toFixed(2), styles: { fontStyle: 'bold', textColor: [255, 255, 255], fillColor: NAVY, fontSize: 9, halign: 'right', cellPadding: { top: 4, bottom: 4, right: 3 } } },
      { content: "", styles: { fillColor: NAVY } }
    ]);

    autoTable(doc, {
      head: [[
        "Date",
        "Bill No",
        "Name",
        "Sales Type",
        { content: "Amount", styles: { halign: 'right' } },
        { content: "Rem. Shakes", styles: { halign: 'right' } }
      ]],
      body: body,
      startY: 31,
      margin: { left: 10, right: 10 },
      theme: 'plain',
      styles: {
        fontSize: 8,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        textColor: [30, 30, 30],
        lineColor: GRAY_LINE,
        lineWidth: 0,
      },
      headStyles: {
        fontStyle: 'bold',
        textColor: [0, 0, 0],
        fillColor: [255, 255, 255],
        lineColor: [0, 0, 0],
        lineWidth: { bottom: 0.5 },
        cellPadding: { top: 4, bottom: 4, left: 3, right: 3 }
      },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 18 },
        2: { cellWidth: 60 },
        3: { cellWidth: 28 },
        4: { cellWidth: 32, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' }
      },
      didDrawPage: (data) => {
        // Footer with page number on every page
        const pg = doc.internal.getCurrentPageInfo().pageNumber;
        const total = doc.internal.getNumberOfPages();
        doc.setFontSize(7.5);
        doc.setTextColor(150);
        doc.text(`Page ${pg} of ${total}`, pageW - 10, doc.internal.pageSize.getHeight() - 7, { align: 'right' });
        doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")}`, 10, doc.internal.pageSize.getHeight() - 7);
      }
    });

    doc.save(`Consolidated_Report_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Consolidated PDF Exported");
    setShowExportMenu(false);
  };

  // Export to PDF (Original Simple Version)
  const exportToPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Admin Recharge Transactions", 14, 22);
    doc.setFontSize(11);
    doc.text(`Generated on: ${new Date().toLocaleDateString("en-IN")}`, 14, 30);

    const tableData = filteredTransactions.map((txn) => [
      txn.orderId || "N/A",
      formatDate(txn.timestamp || txn.createdAt),
      txn.userName || "N/A",
      txn.transaction?.type || "N/A",
      (txn.summary?.grandTotalAmount || 0).toFixed(2),
      txn.transaction?.paymentMethod || "N/A",
    ]);

    autoTable(doc, {
      head: [["Order ID", "Date", "User", "Type", "Amount", "Payment"]],
      body: tableData,
      startY: 35,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
    });

    const fileName = `Recharge_Transactions_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
    toast.success("Exported to PDF");
    setShowExportMenu(false);
  };

  // Print
  const handlePrint = () => {
    window.print();
    setShowExportMenu(false);
  };

  // Calculate totals
  const totals = filteredTransactions.reduce(
    (acc, txn) => ({
      quantity: acc.quantity + (txn.summary?.shakeBalanceChange || 0),
      amount: acc.amount + (txn.summary?.grandTotalAmount || 0),
      vp: acc.vp + (txn.summary?.totalOrderVP || 0),
    }),
    { quantity: 0, amount: 0, vp: 0 }
  );

  const groupedTxns = Object.entries(
    filteredTransactions.slice(0, visibleCount).reduce((acc, txn) => {
      const d = new Date(txn.timestamp || txn.createdAt);
      const dateStr = d.toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' });
      if (!acc[dateStr]) acc[dateStr] = { txns: [], totals: { amount: 0, vp: 0, qty: 0 } };
      acc[dateStr].txns.push(txn);
      acc[dateStr].totals.amount += (txn.summary?.grandTotalAmount || 0);
      acc[dateStr].totals.vp += (txn.summary?.totalOrderVP || 0);
      acc[dateStr].totals.qty += (txn.summary?.shakeBalanceChange || 0);
      return acc;
    }, {})
  );

  return (
    <div className="max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 md:mb-2">
            Admin Recharge Transactions
          </h1>
          <p className="text-[11px] md:text-sm text-gray-600">
            View and manage all recharge transactions
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => toast.info("Schedule Report feature coming soon")}
            className="flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-blue-50 text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-100 transition-all font-medium text-[11px] md:text-sm"
          >
            <Clock className="w-4 h-4" />
            <span className="hidden md:inline">Schedule Report</span>
          </button>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all font-medium text-[11px] md:text-sm"
            >
              <FileDown className="w-4 h-4" />
              <span>Export</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-10">
                <button
                  onClick={exportToExcel}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm first:rounded-t-xl"
                >
                  Export to Excel
                </button>
                <button
                  onClick={exportToPDF}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                >
                  Export to PDF
                </button>
                <button
                  onClick={exportConsolidatedExcel}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                >
                  Consolidated Report (Excel)
                </button>
                <button
                  onClick={exportConsolidatedPDF}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                >
                  Consolidated Report (PDF)
                </button>
                <button
                  onClick={handlePrint}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm last:rounded-b-xl"
                >
                  Print
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 md:gap-4">
          {/* Month Filter */}
          <div>
            <label className="block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5">
              Billing Month
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]"
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5">
              Search
            </label>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="User name, mobile..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]"
              />
            </div>
          </div>

          {/* Transaction Type */}
          <div>
            <label className="block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5">
              Transaction Type
            </label>
            <select
              value={selectedTransactionType}
              onChange={(e) => setSelectedTransactionType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]"
            >
              {transactionTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5">
              Payment Method
            </label>
            <select
              value={selectedPaymentMethod}
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]"
            >
              <option value="All">All Methods</option>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Card">Card</option>
              <option value="Online">Online</option>
            </select>
          </div>

          {/* Date Filter */}
          <div>
            <label className="block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5">
              Date Filter
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]"
            >
              <option value="All">All Time</option>
              <option value="Today">Today</option>
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="Custom">Custom Range</option>
            </select>
          </div>

          {/* From Date */}
          {dateFilter === "Custom" && (
            <div>
              <label className="block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5">
                From Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]"
                />
              </div>
            </div>
          )}

          {/* To Date */}
          {dateFilter === "Custom" && (
            <div>
              <label className="block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5">
                To Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-medium text-[13px]"
            >
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium text-[13px]"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                  User Details
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                  Transaction Type
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                  Products
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-blue-50">
                  Shake Qty
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-orange-50">
                  Rem. Shakes
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-green-50">
                  Amount
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-purple-50">
                  VP
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-6 py-12 text-center">
                    <div className="flex justify-center items-center">
                      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  </td>
                </tr>
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td
                    colSpan="11"
                    className="px-6 py-12 text-center text-gray-500 text-sm"
                  >
                    No transactions found
                  </td>
                </tr>
              ) : (
                groupedTxns.map(([dateStr, group]) => (
                  <Fragment key={dateStr}>
                    {/* Group Header Row */}
                    <tr
                      className="bg-gray-100 cursor-pointer hover:bg-gray-200 border-b border-gray-300"
                      onClick={() => toggleDayCollapse(dateStr)}
                    >
                      <td colSpan="5" className="px-4 py-3 text-[13px] font-bold text-gray-800">
                        <div className="flex items-center gap-2">
                          {collapsedDays[dateStr] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          {dateStr} <span className="text-gray-500 font-normal ml-2">({group.txns.length} transactions)</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-[13px] font-bold text-blue-700 bg-blue-100">{group.totals.qty}</td>
                      <td className="px-4 py-3 bg-orange-100"></td>
                      <td className="px-4 py-3 text-center text-[13px] font-bold text-green-700 bg-green-100">{formatCurrency(group.totals.amount)}</td>
                      <td className="px-4 py-3 text-center text-[13px] font-bold text-purple-700 bg-purple-100">{group.totals.vp.toFixed(2)}</td>
                      <td colSpan="3" className="px-4 py-3 bg-gray-100"></td>
                    </tr>

                    {/* Group Items */}
                    {!collapsedDays[dateStr] && group.txns.map((txn) => (
                      <tr
                        key={txn.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-[13px] font-medium text-blue-600">
                            {txn.orderId || "N/A"}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-[13px] font-medium text-gray-900">
                            {formatDate(txn.timestamp || txn.createdAt)}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-[13px] font-medium text-gray-900">
                            {txn.userName || "N/A"}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {txn.userMobile || "N/A"}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-800">
                            {txn.transaction?.type || "N/A"}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-[13px] text-gray-900 max-w-xs">
                            {txn.items && txn.items.length > 0
                              ? txn.items
                                .map((item) => `${item.productName} (${item.quantity})`)
                                .join(", ")
                              : "N/A"}
                          </div>
                          {txn.dailyHealthRecord && (
                            <div className="flex items-center gap-1 mt-1">
                              <Activity className="w-3 h-3 text-green-600" />
                              <span className="text-[10px] text-green-600">Health Record</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center bg-blue-50">
                          <div className="text-[13px] font-bold text-blue-700">
                            {txn.summary?.shakeBalanceChange > 0 ? '+' : ''}{txn.summary?.shakeBalanceChange || 0}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center bg-orange-50">
                          <div className={`text-[13px] font-bold ${(txn.remainingShakes ?? 0) === 0 ? 'text-red-600' :
                            (txn.remainingShakes ?? 0) <= 3 ? 'text-orange-500' : 'text-orange-700'
                            }`}>
                            {txn.remainingShakes ?? '—'}
                          </div>
                          {(txn.remainingShakes ?? 0) === 0 && (
                            <div className="text-[9px] text-red-500 font-semibold mt-0.5">Empty</div>
                          )}
                          {(txn.remainingShakes ?? 0) > 0 && (txn.remainingShakes ?? 0) <= 3 && (
                            <div className="text-[9px] text-orange-400 font-semibold mt-0.5">Low</div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center bg-green-50">
                          <div className="text-[13px] font-bold text-green-700">
                            {formatCurrency(txn.summary?.grandTotalAmount || 0)}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center bg-purple-50">
                          <div className="text-[13px] font-bold text-purple-700">
                            {(txn.summary?.totalOrderVP || 0).toFixed(2)}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-[13px] text-gray-900 capitalize">
                            {txn.transaction?.paymentMethod || "N/A"}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={`px-3 py-1 inline-flex text-[11px] leading-5 font-semibold rounded-full ${txn.transaction?.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {txn.transaction?.status || "N/A"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => {
                              // Transform order data to invoice format
                              const invoiceData = {
                                billNo: txn.orderId,
                                id: txn.id,
                                userName: txn.userName,
                                userMobile: txn.userMobile,
                                userEmail: txn.userEmail,
                                products: txn.items?.map(item => ({
                                  productName: item.productName,
                                  quantity: item.quantity,
                                  price: item.pricePerUnit,
                                  mrp: item.pricePerUnit,
                                })) || [],
                                totalAmount: txn.summary?.grandTotalAmount || 0,
                                totalShakeQuantity: txn.summary?.shakeBalanceChange || 0,
                                paymentMethod: txn.transaction?.paymentMethod || "N/A",
                                createdAt: txn.timestamp || txn.createdAt,
                                transactionType: txn.transaction?.type,
                                dailySerial: txn.dailySerial,
                              };
                              setSelectedInvoice(invoiceData);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Print Invoice"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditClick(txn)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors ml-2"
                            title="Edit Transaction"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTransaction(txn.id, txn.monthKey, txn.dayKey)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-2"
                            title="Delete Transaction"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))
              )}

              {/* Totals Row */}
              {!loading && filteredTransactions.length > 0 && (
                <tr className="bg-gray-100 border-t-2 border-gray-400 font-bold">
                  <td className="px-4 py-4 whitespace-nowrap" colSpan="5">
                    <div className="text-[13px] font-bold text-gray-900">
                      TOTAL
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center bg-blue-100">
                    <div className="text-[13px] font-bold text-blue-700">
                      {totals.quantity}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center bg-orange-100">
                    <div className="text-[11px] font-semibold text-orange-500 italic">live</div>
                  </td>
                  <td className="px-4 py-4 text-center bg-green-100">
                    <div className="text-[13px] font-bold text-green-700">
                      {formatCurrency(totals.amount)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center bg-purple-100">
                    <div className="text-[13px] font-bold text-purple-700">
                      {totals.vp.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" colSpan="3">
                    <span className="px-3 py-1 inline-flex text-[11px] leading-5 font-semibold rounded-full bg-gray-200 text-gray-800">
                      {filteredTransactions.length} Transactions
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3 md:space-y-4">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center text-gray-500 text-[13px]">
            No transactions found
          </div>
        ) : (
          groupedTxns.map(([dateStr, group]) => (
            <Fragment key={dateStr}>
              <div
                className="flex items-center justify-between bg-gray-100 p-3 rounded-xl cursor-pointer"
                onClick={() => toggleDayCollapse(dateStr)}
              >
                <div className="flex items-center gap-2 font-bold text-[13px] text-gray-800">
                  {collapsedDays[dateStr] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {dateStr} <span className="text-gray-500 font-normal">({group.txns.length})</span>
                </div>
                <div className="flex gap-3 text-[11px] font-bold">
                  <span className="text-green-700">{formatCurrency(group.totals.amount)}</span>
                  <span className="text-purple-700">{group.totals.vp.toFixed(1)} VP</span>
                </div>
              </div>

              {!collapsedDays[dateStr] && group.txns.map((txn) => (
                <div
                  key={txn.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-blue-600 font-medium mb-1">
                        {txn.orderId || "N/A"}
                      </div>
                      <h3 className="text-[13px] md:text-base font-semibold text-gray-900">
                        {txn.userName || "N/A"}
                      </h3>
                      <p className="text-[11px] md:text-sm text-gray-500 mt-1">
                        {txn.userMobile || "N/A"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-800 flex-shrink-0">
                        {txn.transaction?.type || "N/A"}
                      </span>
                      <button
                        onClick={() => {
                          const invoiceData = {
                            billNo: txn.orderId,
                            id: txn.id,
                            userName: txn.userName,
                            userMobile: txn.userMobile,
                            userEmail: txn.userEmail,
                            products: txn.items?.map(item => ({
                              productName: item.productName,
                              quantity: item.quantity,
                              price: item.pricePerUnit,
                              mrp: item.pricePerUnit,
                            })) || [],
                            totalAmount: txn.summary?.grandTotalAmount || 0,
                            totalShakeQuantity: txn.summary?.shakeBalanceChange || 0,
                            paymentMethod: txn.transaction?.paymentMethod || "N/A",
                            createdAt: txn.timestamp || txn.createdAt,
                            transactionType: txn.transaction?.type,
                            dailySerial: txn.dailySerial,
                          };
                          setSelectedInvoice(invoiceData);
                        }}
                        className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-100"
                        title="Print Invoice"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditClick(txn)}
                        className="p-1.5 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors border border-orange-100"
                        title="Edit Transaction"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTransaction(txn.id, txn.monthKey, txn.dayKey)}
                        className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-100"
                        title="Delete Transaction"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <span className="text-[11px] md:text-sm font-medium text-gray-700">
                        Date:
                      </span>
                      <span className="text-[11px] md:text-sm text-gray-600">
                        {formatDate(txn.timestamp || txn.createdAt)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[11px] md:text-sm font-medium text-gray-700">
                        Products:
                      </span>
                      <span className="text-[11px] md:text-sm text-gray-600 text-right">
                        {txn.items && txn.items.length > 0
                          ? txn.items
                            .map((item) => `${item.productName} (${item.quantity})`)
                            .join(", ")
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[11px] md:text-sm font-medium text-gray-700">
                        Shake Qty:
                      </span>
                      <span className="text-[11px] md:text-sm font-bold text-blue-600">
                        {txn.summary?.shakeBalanceChange > 0 ? '+' : ''}{txn.summary?.shakeBalanceChange || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[11px] md:text-sm font-medium text-gray-700">
                        Rem. Shakes:
                      </span>
                      <span className={`text-[11px] md:text-sm font-bold ${(txn.remainingShakes ?? 0) === 0 ? 'text-red-600' :
                        (txn.remainingShakes ?? 0) <= 3 ? 'text-orange-500' : 'text-orange-700'
                        }`}>
                        {txn.remainingShakes ?? '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[11px] md:text-sm font-medium text-gray-700">
                        Amount:
                      </span>
                      <span className="text-[11px] md:text-sm font-bold text-green-600">
                        {formatCurrency(txn.summary?.grandTotalAmount || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[11px] md:text-sm font-medium text-gray-700">
                        VP:
                      </span>
                      <span className="text-[11px] md:text-sm font-bold text-purple-600">
                        {(txn.summary?.totalOrderVP || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[11px] md:text-sm font-medium text-gray-700">
                        Payment:
                      </span>
                      <span className="text-[11px] md:text-sm text-gray-600 capitalize">
                        {txn.transaction?.paymentMethod || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[11px] md:text-sm font-medium text-gray-700">
                        Status:
                      </span>
                      <span
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded-full ${txn.transaction?.status === "completed"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {txn.transaction?.status || "N/A"}
                      </span>
                    </div>
                    {txn.dailyHealthRecord && (
                      <div className="flex items-center gap-1 pt-2 border-t border-gray-100">
                        <Activity className="w-4 h-4 text-green-600" />
                        <span className="text-[11px] text-green-600 font-medium">Health Record Available</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </Fragment>
          ))
        )}
      </div>

      {/* Pagination Footer */}
      {filteredTransactions.length > visibleCount && (
        <div className="flex justify-center mt-6 mb-4">
          <button
            onClick={() => setVisibleCount((prev) => prev + 25)}
            className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Load Next 25 Transactions
          </button>
        </div>
      )}

      {/* Invoice Receipt Modal */}
      {selectedInvoice && (
        <InvoiceReceipt
          transaction={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}

      {/* Edit Transaction Modal */}
      {editingTxn && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">Edit Transaction</h3>
              <button
                onClick={() => setEditingTxn(null)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-xs text-gray-500 font-medium mb-1">Order ID</p>
                <p className="text-sm font-semibold text-gray-900">{editingTxn.orderId}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Transaction Type
                </label>
                <select
                  value={editFormData.type}
                  onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                >
                  {transactionTypes.filter(t => t !== "All").map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Payment Method
                </label>
                <select
                  value={editFormData.paymentMethod}
                  onChange={(e) => setEditFormData({ ...editFormData, paymentMethod: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                  <option value="Online">Online</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Shake Qty
                    <span className="ml-1 text-[10px] text-gray-400 font-normal">(e.g. -1, +25)</span>
                  </label>
                  <input
                    type="number"
                    value={editFormData.shakeBalanceChange}
                    onChange={(e) => setEditFormData({ ...editFormData, shakeBalanceChange: e.target.value })}
                    placeholder="e.g. -1"
                    className="w-full px-3 py-2.5 border border-blue-200 bg-blue-50 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm font-semibold text-blue-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Rem. Shakes
                    <span className="ml-1 text-[10px] text-orange-400 font-normal">(updates user balance)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editFormData.remainingShakesAfter}
                    onChange={(e) => setEditFormData({ ...editFormData, remainingShakesAfter: e.target.value })}
                    placeholder="e.g. 19"
                    className="w-full px-3 py-2.5 border border-orange-200 bg-orange-50 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none text-sm font-semibold text-orange-700"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setEditingTxn(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-lg transition-colors"
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTransaction}
                disabled={isUpdating}
                className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-70"
              >
                {isUpdating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRechargeList;